/**
 * dashboard-buckets — the SINGLE config→grid-items bucketing shared by
 * `DashboardBody` (real render) and `DashboardSkeleton` (loading
 * blocks). One function owns the section grouping, the item keys, the
 * width/order defaults, the hidden filter, the height resolution and
 * the legacy singular table condition — so the skeleton can never
 * drift from where the real content lands (uniform-grid review #7).
 *
 * Identity contract (review #1):
 *   * chart keys: `chart-${item_id}` when the flattener stamped an
 *     authoring id (builder Preview), else `chart-idx-${configIndex}`
 *     — a STABLE index fallback for engine payloads that don't carry
 *     item ids. This is render identity, never a data fallback: two
 *     same-titled charts get distinct keys either way.
 *   * table keys: `table-${id}` (ids are the fetch identity), with the
 *     same index fallback for id-less configs.
 *   * text keys: `text-${configIndex}`.
 *   Duplicate keys (corrupt config that bypassed the publish-time
 *   item-id uniqueness check) are caught by `placeItems`, which throws
 *   → per-section error card.
 *
 * Height resolution can THROW (explicit height > MAX_GRID_H — see
 * `grid-layout.resolveHeight`). The bucketing catches it PER SECTION
 * and marks the bucket with `error` so one poisoned section renders a
 * visible error card while the rest of the dashboard stays healthy.
 */

import { MAIN_TABLE_ID } from "./dashboard-data";
import { resolveHeight } from "./grid-layout";
import type { DisplayLabel } from "./i18n-resolve";
import type {
  ChartConfig,
  DashboardRenderConfig,
  DrillConfig,
  KpiConfig,
  TableConfig,
  TextConfig,
} from "./types";

/** One KPI slot: the config entry + its index into the flattened
 *  `config.kpis` list (the engine's `fetch_kpis` result is matched by
 *  the same index). */
export interface KpiSlot {
  cfg: KpiConfig;
  idx: number;
}

/** One grid item derived from the config (pre-data). */
export interface GridItemSpec {
  key: string;
  kind: "chart" | "table" | "text" | "kpi";
  order: number;
  width: number;
  /** Resolved grid height in ROW UNITS (same resolution table as the
   *  engine — `grid-layout.resolveHeight`). */
  heightUnits: number;
  /** Authoring item id (builder inspector; absent in the portal). */
  itemId?: string;
  /** KPI items placed IN the grid (``in_grid``) instead of the strip.
   *  Carries the same (cfg, idx) pair the strip uses, so both paths
   *  render through the identical card. */
  kpi?: { cfg: KpiConfig; idx: number };
  /** Chart items carry their index into the flattened ``config.charts``
   *  list — the DATA identity (PR-3 §2): the engine returns exactly one
   *  ``ChartData`` per config chart in config order, so
   *  ``chartsData[idx]`` is THIS item's payload. */
  chart?: { cfg: ChartConfig; idx: number };
  /** Table items carry the fetch identity + the row-drill panel's
   *  metadata so the render layer never re-derives them. */
  table?: {
    cfg: TableConfig;
    tableId: string;
    drillId: string | null;
    drillCfg: DrillConfig | null;
  };
  text?: { cfg: TextConfig; idx: number };
}

export interface SectionBucketSpec {
  id: string;
  /** First-writer-wins across the section's items (same semantics the
   *  body always had — the flatten stamps identical titles on every
   *  item of a section, so "first" is also "all"). */
  title: DisplayLabel;
  subtitle: DisplayLabel;
  /** Section-level tooltip (config ``sections[].info``, stamped as
   *  ``section_info`` on every item by both flattens). */
  info: DisplayLabel;
  order: number;
  kpis: KpiSlot[];
  /** Sorted by `order` (authoring order after the flatten stamp). */
  grid: GridItemSpec[];
  /** Set when height resolution failed for an item in this section —
   *  the consumers render a visible error card instead of the grid
   *  (fail loud, scoped to the section). */
  error?: string;
}

export interface BucketedDashboard {
  sections: SectionBucketSpec[];
  /** The legacy singular table (dashboard_explorer) — non-null exactly
   *  when the body renders it: no named tables AND the singular table
   *  declares at least one visible column. Rendered OUTSIDE the grid
   *  at natural height; the skeleton mirrors it with an upper-bound
   *  block. */
  legacyTable: TableConfig | null;
}

/** Visual reading order: rows first, then columns. With the current
 *  forward-only placement cursor the simulator already emits
 *  placements in this order, but the sort makes the DOM-order ==
 *  visual-order invariant EXPLICIT (a11y: tab/reading order follows
 *  the layout even if the placement algorithm changes). */
export function byVisualOrder(
  byKey: ReadonlyMap<string, { rowStart: number; colStart: number }>,
): (a: { key: string }, b: { key: string }) => number {
  return (a, b) => {
    const pa = byKey.get(a.key);
    const pb = byKey.get(b.key);
    if (!pa || !pb) return 0;
    return pa.rowStart - pb.rowStart || pa.colStart - pb.colStart;
  };
}

/**
 * Bucket a flattened dashboard config into per-section grid items.
 * CONFIG-driven (no data needed) so the skeleton can run it before any
 * fetch lands; `DashboardBody` attaches the fetched data to the same
 * specs — everything by CONFIG INDEX (charts via the spec's `idx`,
 * KPIs/texts via their slot index).
 */
export function bucketDashboardConfig(
  config: DashboardRenderConfig,
): BucketedDashboard {
  const buckets = new Map<string, SectionBucketSpec>();
  const ensure = (
    id: string,
    title: DisplayLabel,
    subtitle: DisplayLabel,
    info: DisplayLabel,
    ord: number,
  ): SectionBucketSpec => {
    const existing = buckets.get(id);
    if (existing) {
      existing.order = Math.min(existing.order, ord);
      return existing;
    }
    const created: SectionBucketSpec = {
      id,
      title,
      subtitle,
      info,
      order: ord,
      kpis: [],
      grid: [],
    };
    buckets.set(id, created);
    return created;
  };
  /** Push with per-section fail-loud isolation: a throwing height
   *  resolution poisons ONLY its section (error card), never the page. */
  const push = (bucket: SectionBucketSpec, make: () => GridItemSpec): void => {
    if (bucket.error) return;
    try {
      bucket.grid.push(make());
    } catch (e) {
      bucket.error = e instanceof Error ? e.message : String(e);
    }
  };

  (config.kpis ?? []).forEach((cfg, i) => {
    // A hidden KPI is computed (its payload entry keeps index `i`) but
    // never drawn, and does not open a section of its own.
    if (cfg.hidden) return;
    // Section-order seed `cfg.order ?? 0` — the body's historical seed
    // (the skeleton used `?? i` before unification; review #7c).
    const bucket = ensure(
      cfg.section_id ?? "",
      cfg.section_title ?? "",
      cfg.section_subtitle ?? "",
      cfg.section_info ?? "",
      cfg.order ?? 0,
    );
    if (cfg.in_grid !== true) {
      bucket.kpis.push({ cfg, idx: i });
      return;
    }
    // `in_grid`: el KPI deja la TIRA y entra al grid como una celda
    // más. La tira siempre va arriba y a todo el ancho, así que un
    // reporte no podía poner sus métricas EN COLUMNA al costado de una
    // tabla — que es la forma del reporte de referencia: la tabla
    // grande a la izquierda y el resumen apilado a la derecha. En el
    // grid el orden de los items y el empaquetado hacen el resto.
    push(bucket, () => ({
      key: `kpi-${cfg.item_id ?? `idx-${i}`}`,
      kind: "kpi",
      order: cfg.order ?? 0,
      width: cfg.width ?? 3,
      heightUnits: resolveHeight({
        element: "kpi",
        height: cfg.height ?? null,
        rowSpan: cfg.row_span ?? null,
      }),
      itemId: cfg.item_id,
      kpi: { cfg, idx: i },
    }));
  });

  (config.charts ?? []).forEach((cfg, i) => {
    // Drill-swap charts (``swap_on``) never occupy their own cell —
    // they render INSIDE their target's cell while the driving filter
    // is active (DashboardBody GridCell). Positional chart-data pairing
    // is unaffected: specs carry the config index.
    if (cfg.swap_on) return;
    const bucket = ensure(
      cfg.section_id ?? "",
      cfg.section_title ?? "",
      cfg.section_subtitle ?? "",
      cfg.section_info ?? "",
      cfg.order ?? i,
    );
    push(bucket, () => ({
      key: `chart-${cfg.item_id ?? `idx-${i}`}`,
      kind: "chart",
      order: cfg.order ?? i,
      width: cfg.width ?? (cfg.type === "table" ? 12 : 4),
      heightUnits: resolveHeight({
        element: "chart",
        chartType: cfg.type,
        mapVariant: cfg.map_variant,
        height: cfg.height,
        rowSpan: cfg.row_span,
      }),
      itemId: cfg.item_id,
      chart: { cfg, idx: i },
    }));
  });

  (config.tables ?? [])
    .filter((cfg) => !cfg.hidden)
    .forEach((cfg, i) => {
      const bucket = ensure(
        cfg.section_id ?? "",
        cfg.section_title ?? "",
        cfg.section_subtitle ?? "",
        cfg.section_info ?? "",
        cfg.order ?? 1000 + i,
      );
      const drillCfg =
        (cfg.row_drill_target && config.drills
          ? config.drills.find((d) => d.drill_id === cfg.row_drill_target)
          : null) ?? null;
      push(bucket, () => ({
        key: `table-${cfg.id || `idx-${i}`}`,
        kind: "table",
        order: cfg.order ?? 1000 + i,
        width: cfg.width ?? 12,
        heightUnits: resolveHeight({
          element: "table",
          height: cfg.height,
          rowSpan: cfg.row_span,
        }),
        itemId: cfg.item_id,
        table: {
          cfg,
          tableId: cfg.id || MAIN_TABLE_ID,
          drillId: cfg.row_drill_target ?? null,
          drillCfg,
        },
      }));
    });

  (config.texts ?? []).forEach((cfg, i) => {
    // Section-order seed: the authored canvas order when the engine
    // carries it (TextConfig.order, 2026-10-22), else the body's
    // historical plain `i` (review #7c). With `i` alone, a section whose
    // first item is the SECOND text of the dashboard seeded order 1 and
    // was hoisted right under the KPI strip (Share of Search).
    const bucket = ensure(
      cfg.section_id ?? "",
      cfg.section_title ?? "",
      cfg.section_subtitle ?? "",
      cfg.section_info ?? "",
      cfg.order ?? i,
    );
    push(bucket, () => ({
      key: `text-${i}`,
      kind: "text",
      order: cfg.order ?? i,
      width: 12,
      heightUnits: resolveHeight({ element: "text", height: cfg.height }),
      itemId: cfg.item_id,
      text: { cfg, idx: i },
    }));
  });

  const sections = Array.from(buckets.values()).sort((a, b) => a.order - b.order);
  for (const b of sections) b.grid.sort((a, c) => a.order - c.order);

  const hasNamedTables = (config.tables?.length ?? 0) > 0;
  const legacyTable =
    !hasNamedTables &&
    config.table &&
    (config.table.columns?.filter((c) => !c.hidden).length ?? 0) > 0
      ? config.table
      : null;

  return { sections, legacyTable };
}
